import React, { useState } from "react";
import { Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import callingleads from "../../assets/images/project_creation_format.csv";
import {
  FaFileDownload,
  FaFileCsv,
  FaUpload,
  FaCheckCircle,
  FaInfoCircle,
} from "react-icons/fa";

const API_URL = process.env.REACT_APP_API_URL;

function CreateProjectByUploadingCSV() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);

  const getAuthToken = () => localStorage.getItem("token");

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      Swal.fire({
        icon: "error",
        title: "No CSV File",
        text: "Please select a CSV file before submitting.",
      });
      return;
    }
    setLoading(true);
    try {
      const token = getAuthToken();
      if (!token) {
        Swal.fire({
          icon: "error",
          title: "Authentication Error",
          text: "Authentication token not found. Please log in.",
        });
        return;
      }

      const uploadData = new FormData();
      uploadData.append("projectCsv", file);

      const response = await fetch(`${API_URL}/project-import`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: uploadData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorData.message || "Failed to import associates.",
        });
        return;
      }

      const result = await response.json();

      if (result.status == "success") {
        if (result.summary && result.summary.imported === 0) {
          Swal.fire({
            icon: "warning",
            title: "No Data Imported",
            text: "Sorry, no data was imported. Please check your CSV file or duplicate entry and try again.",
            confirmButtonText: "OK",
          });
        } else {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: `Successfully imported ${result.summary?.imported || 0} projects!`,
            confirmButtonText: "OK",
          });

          setFile(null);
          document.querySelector('input[type="file"]').value = "";

          setTimeout(() => {
            navigate("/all-project");
          }, 2000);
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: result.message || "Something went wrong!",
          confirmButtonText: "OK",
        });
      }
    } catch (err) {
      console.error("Error importing projects:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "An unexpected error occurred.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f0f6ff 0%, #e6f0fa 100%)",
        padding: "30px 24px",
        fontFamily: "'Poppins', 'DM Sans', sans-serif",
      }}
    >
      <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
        {/* 🎯 HEADER CARD (Baahar Alag Card) */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "16px",
            boxShadow: "0 8px 24px rgba(30, 87, 153, 0.08)",
            border: "1.5px solid #e2e8f0",
            padding: "20px 24px",
            display: "flex",
            alignItems: "center",
            gap: "16px",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              width: "52px",
              height: "52px",
              borderRadius: "14px",
              background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ffffff",
              fontSize: "24px",
              boxShadow: "0 8px 20px rgba(30, 87, 153, 0.3)",
              flexShrink: 0,
            }}
          >
            <FaFileCsv />
          </div>
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: "24px",
                fontWeight: "800",
                color: "#1e3a5f",
                letterSpacing: "-0.5px",
              }}
            >
              Create New Project
            </h1>
            <p
              style={{
                margin: "2px 0 0",
                fontSize: "13px",
                color: "#64748b",
                fontWeight: "500",
              }}
            >
              Upload a CSV file to create projects in your system
            </p>
          </div>
        </div>

        {/* 🎯 MAIN CARD (Form Ke Saath) */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "16px",
            boxShadow: "0 8px 24px rgba(30, 87, 153, 0.08)",
            border: "1.5px solid #e2e8f0",
            overflow: "hidden",
          }}
        >
          {/* Top Gradient Bar */}
          <div
            style={{
              height: "5px",
              background: "linear-gradient(90deg, #1e5799, #2b6cb0, #1e5799)",
            }}
          />

          <Form onSubmit={handleSubmit} style={{ padding: "30px 30px 24px" }}>
            {/* Info Notice */}
            <div
              style={{
                background: "#e8f1fb",
                border: "1.5px solid #c7ddf3",
                borderRadius: "12px",
                padding: "14px 18px",
                marginBottom: "24px",
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  background: "#1e5799",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  flexShrink: 0,
                }}
              >
                <FaInfoCircle />
              </div>
              <div style={{ flex: 1 }}>
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    fontWeight: "700",
                    color: "#1e3a5f",
                  }}
                >
                  Important Instructions
                </p>
                <p
                  style={{
                    margin: "4px 0 0",
                    fontSize: "12.5px",
                    color: "#475569",
                    lineHeight: "1.6",
                    fontWeight: "500",
                  }}
                >
                  Please download the CSV format first, fill in your project
                  details, and then upload the file. Only <strong>.csv</strong>{" "}
                  files are allowed (max 10MB).
                </p>
              </div>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    Download Create New Project By Uploading CSV Excel Format
                  </Tooltip>
                }
              >
                <a
                  href={callingleads}
                  download="Project Creation Format.csv"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "8px 14px",
                    borderRadius: "8px",
                    background: "#ffffff",
                    color: "#1e5799",
                    fontSize: "12px",
                    fontWeight: "700",
                    textDecoration: "none",
                    border: "1.5px solid #c7ddf3",
                    transition: "all 0.25s ease",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#1e5799";
                    e.currentTarget.style.color = "#ffffff";
                    e.currentTarget.style.borderColor = "#1e5799";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#ffffff";
                    e.currentTarget.style.color = "#1e5799";
                    e.currentTarget.style.borderColor = "#c7ddf3";
                  }}
                >
                  <FaFileDownload />
                  Download
                </a>
              </OverlayTrigger>
            </div>

            {/* File Upload Area */}
            <div
              style={{
                border: file ? "2px solid #059669" : "2px dashed #c7ddf3",
                borderRadius: "16px",
                padding: "40px 24px",
                background: file ? "#e7f9eb" : "#f8fafc",
                textAlign: "center",
                transition: "all 0.3s ease",
                cursor: "pointer",
                marginBottom: "24px",
              }}
              onClick={() => document.getElementById("csvFileInput").click()}
            >
              <input
                id="csvFileInput"
                type="file"
                accept=".csv"
                style={{ display: "none" }}
                onChange={(e) => {
                  const selectedFile = e.target.files[0];
                  if (!selectedFile) return;

                  const fileExtension = selectedFile.name
                    .split(".")
                    .pop()
                    .toLowerCase();
                  if (fileExtension !== "csv") {
                    Swal.fire({
                      icon: "error",
                      title: "Invalid File",
                      text: "Only CSV files are allowed!",
                    });
                    e.target.value = null;
                    setFile(null);
                    return;
                  }
                  setFile(selectedFile);
                }}
              />

              {!file ? (
                <>
                  <div
                    style={{
                      width: "64px",
                      height: "64px",
                      borderRadius: "18px",
                      background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 18px",
                      fontSize: "28px",
                      color: "#ffffff",
                      boxShadow: "0 8px 24px rgba(30, 87, 153, 0.3)",
                    }}
                  >
                    <FaUpload />
                  </div>
                  <h4
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "800",
                      color: "#1e3a5f",
                    }}
                  >
                    Click to Upload CSV File
                  </h4>
                  <p
                    style={{
                      margin: "6px 0 0",
                      fontSize: "12.5px",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    or drag and drop your file here
                  </p>
                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: "11.5px",
                      color: "#94a3b8",
                      fontWeight: "600",
                    }}
                  >
                    Only .csv files · Max 10MB
                  </p>
                </>
              ) : (
                <>
                  <div
                    style={{
                      width: "64px",
                      height: "64px",
                      borderRadius: "18px",
                      background: "linear-gradient(135deg, #059669, #047857)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 18px",
                      fontSize: "28px",
                      color: "#ffffff",
                      boxShadow: "0 8px 24px rgba(5, 150, 105, 0.3)",
                    }}
                  >
                    <FaCheckCircle />
                  </div>
                  <h4
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "800",
                      color: "#059669",
                    }}
                  >
                    File Selected Successfully
                  </h4>
                  <p
                    style={{
                      margin: "8px 0 0",
                      fontSize: "13px",
                      color: "#1e3a5f",
                      fontWeight: "600",
                      wordBreak: "break-all",
                    }}
                  >
                    📄 {file.name}
                  </p>
                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: "11.5px",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    {(file.size / 1024).toFixed(2)} KB · Click to change file
                  </p>
                </>
              )}
            </div>

            {/* Footer — Note + Button */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingTop: "20px",
                borderTop: "1.5px dashed #e2e8f0",
                gap: "16px",
                flexWrap: "wrap",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: "12.5px",
                  color: "#94a3b8",
                  fontWeight: "500",
                }}
              >
                🔒 Only .csv files allowed. Max size: 10MB
              </p>

              <button
                type="submit"
                disabled={loading || !file}
                style={{
                  padding: "13px 42px",
                  borderRadius: "12px",
                  border: "none",
                  background:
                    loading || !file
                      ? "#cbd5e1"
                      : "linear-gradient(135deg, #1e5799, #2b6cb0)",
                  color: "#ffffff",
                  fontSize: "14.5px",
                  fontWeight: "800",
                  letterSpacing: "0.5px",
                  cursor: loading || !file ? "not-allowed" : "pointer",
                  transition: "all 0.3s ease",
                  boxShadow:
                    loading || !file
                      ? "none"
                      : "0 8px 24px rgba(30, 87, 153, 0.3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  minWidth: "220px",
                }}
                onMouseEnter={(e) => {
                  if (!loading && file) {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 12px 32px rgba(30, 87, 153, 0.45)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading && file) {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 24px rgba(30, 87, 153, 0.3)";
                  }
                }}
              >
                {loading ? (
                  <>
                    <span
                      style={{
                        width: "16px",
                        height: "16px",
                        border: "2px solid rgba(255,255,255,0.3)",
                        borderTop: "2px solid #ffffff",
                        borderRadius: "50%",
                        display: "inline-block",
                        animation: "spin 0.8s linear infinite",
                      }}
                    />
                    Importing...
                  </>
                ) : (
                  <>
                    <FaUpload />
                    Import Projects
                  </>
                )}
              </button>
            </div>
          </Form>
        </div>

        {/* 🎯 Footer Copyright */}
        <p
          style={{
            textAlign: "center",
            color: "#94a3b8",
            fontSize: "12.5px",
            marginTop: "24px",
            fontWeight: "500",
          }}
        >
          © {new Date().getFullYear()} A2 Demo Real Estates Pvt Ltd. All Rights
          Reserved.
        </p>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default CreateProjectByUploadingCSV;